import type ItineraryPackage from '@itinerary/ItineraryPackage';
import {
    type FreeServiceItem,
    type OptionalServiceItem,
} from '@itinerary/types';
import { type PaxType } from '@utils/types';
import { type UserFormFields } from '@components/util/UserForm.vue';

export type SavedItinerary = {
    adultCount: number;
    childCount: number;
    infantCount: number;
    isFloatingReserve: boolean;
    itineraryPackages: ItineraryPackage[];
    isTrainCaptchaEnabled?: boolean;
    emptyPackage: ItineraryPackage | null;
};

/**
 * Gender of passenger (male or female)
 */
export enum Gender {
    Male,
    Female,
    None,
}

export const genderTitles = {
    [Gender.Male]: 'آقای',
    [Gender.Female]: 'خانم',
    [Gender.None]: 'نامشخص',
};

/**
 * Nationality of passenger (iranian or foreigner)
 */
export enum Nationality {
    Iranian = 'iranian',
    Foreigner = 'foreigner',
    None = 'none',
}

/**
 * Information of passenger
 */
export interface Passenger {
    /**
     * Gender of the passenger
     *
     * @type {Gender}
     * @memberof Passenger
     */
    gender: Gender;

    /**
     * First name of the passengers (english)
     *
     * @type {string}
     * @memberof Passenger
     */
    firstName: string;

    /**
     * Last name of the passengers (english)
     *
     * @type {string}
     * @memberof Passenger
     */
    lastName: string;

    /**
     * First name of the passengers (persian)
     *
     * @type {string}
     * @memberof Passenger
     */
    persianFirstName: string;

    /**
     * Last name of the passengers (persian)
     *
     * @type {string}
     * @memberof Passenger
     */
    persianLastName: string;

    /**
     * Nationality of the passenger
     *
     * @type {Nationality}
     * @memberof Passenger
     */
    nationality: Nationality;

    /**
     * Nationality code of the passenger
     *
     * @type {string | null}
     * @memberof Passenger
     */
    nationalityCode: string | null;

    /**
     * National code of the passenger
     *
     * @type {string}
     * @memberof Passenger
     */
    nationalCode: string;

    /**
     * Birth date of the passenger
     *
     * @type {Date | null}
     * @memberof Passenger
     */
    birthDate: Date | null;

    /**
     * Passport number of the passenger
     *
     * @type {string}
     * @memberof Passenger
     */
    passportNumber: string;

    /**
     * Expiration date of the passenger's passport
     *
     * @type {Date | null}
     * @memberof Passenger
     */
    passportExpiry: Date | null;

    /**
     * Type of the passenger
     *
     * @type {PaxType}
     * @memberof Passenger
     */
    type: PaxType;

    /**
     * Title of the passenger
     *
     * @type {string}
     * @memberof Passenger
     */
    title: string;
}

export interface ItineraryBuyerPassenger {
    /**
     * Model of the passenger's car
     *
     * @type {string}
     * @memberof Passenger
     */
    carModel: string;

    /**
     * Car plate of the passenger
     *
     * @type {string[]}
     * @memberof Passenger
     */
    carPlate: string[];
}

export type SubmitBuyerPassenger = SubmitPassenger & ItineraryBuyerPassenger;

export interface ProfilePassenger {
    email: string;
    phone: string;
    mobile: string;
    companyName: string;
    residenceProvince: string;
    acquaintanceMethodId: number;
    whyMrbilitId: number;
}

export type SubmitProfilePassenger = SubmitPassenger & ProfilePassenger;

export type SubmitPassenger = Omit<
    Passenger,
    'birthDate' | 'passportExpiry'
> & {
    birthDate: string | null;
    passportExpiry: string | null;
    isDuplicated?: boolean;

    /**
     * Optional services selected by passenger
     *
     * @type {number | null}
     * @memberof Passenger
     */
    packageOptionalService: ItineraryPackageService[];
    packageFreeService: ItineraryPackageFreeServices[];
    infantSeat?: boolean;
};

export interface ItineraryPackageService {
    service?: OptionalServiceItem;
    itineraryPackage: ItineraryPackage;
}

export interface ItineraryPackageFreeServices {
    services?: FreeServiceItem[];
    itineraryPackage: ItineraryPackage;
}

/**
 * Indicates which fields of information about passenger the form contains
 */

export enum PassengerFields {
    None = 0b0,
    Gender = 0b1,
    FirstName = 0b10,
    LastName = 0b100,
    PersianFirstName = 0b1000,
    PersianLastName = 0b10000,
    Nationality = 0b100000,
    NationalityCode = 0b1000000,
    BirthDate = 0b10000000,
    PassportNumber = 0b100000000,
    PassportExpiry = 0b1000000000,
    NationalCode = 0b10000000000,
    All = 0b11111111111,
}

export enum BuyExtraFields {
    None = 0b0,
    CarModel = 0b1,
    CarPlate = 0b10,
    PackageOptionalService = 0b100,
    HotelEarlyCheckin = 0b1000,
    HotelLateCheckout = 0b10000,
    PackageFreeService = 0b100000,
    InfantSeat = 0b1000000,
    All = 0b1111111,
}

export enum ProfileExtraFields {
    None = 0b0,
    Mobile = 0b1,
    Phone = 0b10,
    Email = 0b100,
    CompanyName = 0b1000,
    AcquaintanceMethodId = 0b10000,
    WhyMrbilitId = 0b100000,
    ResidenceProvince = 0b1000000,
    All = 0b1111111,
}
/**
 * Indicates which contact fields the form contains
 */
export enum ContactField {
    None = 0,
    Mobile = 1,
    DestinationMobile = 2,
    Email = 4,
    Phone = 8,
    All = 15,
}

/**
 * Information of passengers previously bought ticket for them
 */
export interface PrevPax extends UserFormFields {
    id: number;
    paxType: PaxType;
}

export interface PrevPassenger {
    FirstName: string;
    LastName: string;
    PersianFirstName: string;
    PersianLastName: string;
    NationalCode: string;
    PassportNumber: string;
    PassportExpiration: number;
    BirthDay: string;
    Male: number;
    Id: string;
}

export type UserPassengerForm = Omit<
    Passenger,
    'birthDate' | 'passportExpiry' | 'passportNumber'
> & {
    birthDate: Date | null;
    passportExpiry: Date | null;
    passportNumber: string | null;
    undef: undefined;
};
